/**
 * ════════════════════════════════════════════════════════════════════
 * Intelligent Routing Panel — v3.2.20
 * ════════════════════════════════════════════════════════════════════
 * Admin dashboard panel showing:
 *   - Live routing stats (24h aggregates)
 *   - Recent routing decisions (live feed)
 *   - Agent specialty bindings (35 agents)
 *   - Per-decision hallucination audit drill-down
 *
 * Designed to be embedded in AdminDashboard as a new tab.
 */
import { useEffect, useState } from 'react';
import {
  intelligentApi,
  RoutingStats,
  RoutingDecisionRow,
  AgentSpecialtyRow,
} from '../services/intelligentApi';

type Tab = 'overview' | 'decisions' | 'agents';

export function IntelligentRoutingPanel() {
  const [tab, setTab] = useState<Tab>('overview');
  const [stats, setStats] = useState<RoutingStats | null>(null);
  const [decisions, setDecisions] = useState<RoutingDecisionRow[]>([]);
  const [agents, setAgents] = useState<AgentSpecialtyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDecisionId, setSelectedDecisionId] = useState<string | null>(null);
  const [decisionDetail, setDecisionDetail] = useState<any>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === 'overview') {
        const s = await intelligentApi.getStats();
        setStats(s);
      } else if (tab === 'decisions') {
        const d = await intelligentApi.getDecisions(50);
        setDecisions(d.decisions || []);
      } else if (tab === 'agents') {
        const a = await intelligentApi.getAgents();
        setAgents(a.agents || []);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // Auto-refresh every 30 seconds when on overview or decisions tabs
    const id = setInterval(() => {
      if (tab === 'overview' || tab === 'decisions') refresh();
    }, 30_000);
    return () => clearInterval(id);
  }, [tab]);

  useEffect(() => {
    if (!selectedDecisionId) {
      setDecisionDetail(null);
      return;
    }
    intelligentApi
      .getDecisionDetail(selectedDecisionId)
      .then((d) => setDecisionDetail(d))
      .catch((err) => setError(err.response?.data?.error || err.message));
  }, [selectedDecisionId]);

  return (
    <div className="irol-container" style={styles.container}>
      <div className="irol-header" style={styles.header}>
        <h2 className="irol-title" style={styles.title}>Intelligent Routing Layer (IROL)</h2>
        <div className="irol-header-actions" style={styles.headerActions}>
          <button style={styles.refreshBtn} onClick={refresh} disabled={loading}>
            {loading ? 'Refreshing…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      <div className="irol-tabs" style={styles.tabs}>
        <button
          style={tab === 'overview' ? styles.tabActive : styles.tab}
          onClick={() => setTab('overview')}
        >
          Overview
        </button>
        <button
          style={tab === 'decisions' ? styles.tabActive : styles.tab}
          onClick={() => setTab('decisions')}
        >
          Recent Decisions
        </button>
        <button
          style={tab === 'agents' ? styles.tabActive : styles.tab}
          onClick={() => setTab('agents')}
        >
          Agent Specialties ({agents.length || 35})
        </button>
      </div>

      {error && <div style={styles.errorBox}>Error: {error}</div>}

      {tab === 'overview' && stats && (
        <OverviewTab stats={stats} />
      )}

      {tab === 'decisions' && (
        <DecisionsTab
          decisions={decisions}
          selectedId={selectedDecisionId}
          onSelect={setSelectedDecisionId}
          detail={decisionDetail}
        />
      )}

      {tab === 'agents' && <AgentsTab agents={agents} />}

      <div className="irol-footer" style={styles.footer}>
        <span style={styles.footerText}>
          v3.2.20 — Intelligent Routing Layer. Every AI response in every portal
          goes through intent detection → specialty validation → multi-agent
          fan-out (when needed) → hallucination guard verification.
        </span>
      </div>
    </div>
  );
}

function OverviewTab({ stats }: { stats: RoutingStats }) {
  return (
    <div>
      <div className="irol-stats-grid" style={styles.statsGrid}>
        <StatCard
          label="Total Routing Decisions (24h)"
          value={stats.totalDecisions}
          color="#46739f"
        />
        <StatCard
          label="Multi-Agent Fan-Outs"
          value={stats.multiAgentDecisions}
          color="#4d8760"
          sublabel={`${pct(stats.multiAgentDecisions, stats.totalDecisions)} of total`}
        />
        <StatCard
          label="COPILOT Fallbacks"
          value={stats.copilotFallbacks}
          color="#a58340"
          sublabel={`${pct(stats.copilotFallbacks, stats.totalDecisions)} of total`}
        />
        <StatCard
          label="Specialty Violations"
          value={stats.specialtyViolations}
          color={stats.specialtyViolations > 0 ? '#a9544c' : '#4d8760'}
        />
        <StatCard
          label="Hallucination Blocks"
          value={stats.hallucinationBlocks}
          color={stats.hallucinationBlocks > 10 ? '#a9544c' : '#4d8760'}
        />
        <StatCard
          label="Avg Routing Duration (ms)"
          value={Math.round(stats.avgDurationMs)}
          color="#766d54"
        />
      </div>

      <div className="irol-section" style={styles.section}>
        <h3 style={styles.sectionTitle}>How the routing layer works</h3>
        <ol style={styles.orderedList}>
          <li>
            <b>Intent detection</b> — The request text is scanned against
            the agent specialty map (35 agents, ~250 keywords). The
            highest-priority matching specialists are selected.
          </li>
          <li>
            <b>Specialty validation</b> — Each selected agent is checked
            against its declared specialty. If a request would force an
            agent outside its domain, the routing is rejected.
          </li>
          <li>
            <b>Routing mode</b> — If 2+ eligible specialists from distinct
            domains match, multi-agent fan-out is triggered. Otherwise
            single-agent dispatch is used. If no specialist matches,
            COPILOT fallback is used.
          </li>
          <li>
            <b>Hallucination guard</b> — Every response (single, fan-out,
            or synthesized) is verified against 5 hard checks: source
            exists, confidence floor, fabrication markers, PII leak, and
            output length. The existing SafetyEngine.checkHallucination
            is also called for full safety logic.
          </li>
          <li>
            <b>Audit persistence</b> — Every routing decision and every
            hallucination check is persisted to the
            <code>intelligent_routing_decisions</code> and
            <code>intelligent_routing_audit</code> tables for orchestrator
            review.
          </li>
        </ol>
      </div>

      <div className="irol-section" style={styles.section}>
        <h3 style={styles.sectionTitle}>Orchestrator oversight (every 5 minutes)</h3>
        <p style={styles.bodyText}>
          The EAOE Orchestrator runs <b>8 oversight checks</b> every 5 minutes.
          The original 4 (agent health, circuit breakers, pending approvals,
          failed pipelines) plus 4 new IROL-specific checks added in v3.2.20:
        </p>
        <ul style={styles.bulletList}>
          <li><b>Check 5 — Routing consistency</b>: error rate per triggered_by source</li>
          <li><b>Check 6 — Hallucination rate</b>: block rate per agent, last 24h</li>
          <li><b>Check 7 — Specialty violations</b>: agents responding outside their domain</li>
          <li><b>Check 8 — Multi-agent coverage</b>: copilot-fallback rate (intent detection health)</li>
        </ul>
      </div>
    </div>
  );
}

function DecisionsTab({
  decisions,
  selectedId,
  onSelect,
  detail,
}: {
  decisions: RoutingDecisionRow[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  detail: any;
}) {
  if (decisions.length === 0) {
    return <div style={styles.empty}>No routing decisions recorded yet.</div>;
  }
  return (
    <div className="irol-decisions-layout" style={styles.decisionsLayout}>
      <div className="irol-decisions-list" style={styles.decisionsList}>
        <div className="irol-table-wrap"><table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Time</th>
              <th style={styles.th}>Triggered By</th>
              <th style={styles.th}>Mode</th>
              <th style={styles.th}>Agents</th>
              <th style={styles.th}>Violations</th>
              <th style={styles.th}>Duration</th>
            </tr>
          </thead>
          <tbody>
            {decisions.map((d) => (
              <tr
                key={d.id}
                style={
                  selectedId === d.id
                    ? { ...styles.tr, backgroundColor: '#eef2f7' }
                    : styles.tr
                }
                onClick={() => onSelect(d.id)}
              >
                <td style={styles.td}>{new Date(d.started_at).toLocaleString()}</td>
                <td style={styles.tdMono}>{d.triggered_by}</td>
                <td style={styles.td}>
                  <span className="irol-mode-badge" style={modeBadge(d.routing_mode)}>{d.routing_mode}</span>
                </td>
                <td style={styles.tdMono}>{(d.selected_agents || []).join(', ')}</td>
                <td style={styles.td}>
                  {Array.isArray(d.specialty_violations) && d.specialty_violations.length > 0 ? (
                    <span className="irol-status-badge" style={styles.violationBadge}>{d.specialty_violations.length}</span>
                  ) : (
                    <span className="irol-status-badge" style={styles.okBadge}>0</span>
                  )}
                </td>
                <td style={styles.tdMono}>{d.duration_ms ?? '—'}ms</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>
      {detail && (
        <div className="irol-detail-panel" style={styles.detailPanel}>
          <h4 style={styles.detailTitle}>Decision Detail</h4>
          <div style={styles.detailSection}>
            <strong>Request:</strong>
            <pre style={styles.pre}>{detail.decision?.request_text}</pre>
          </div>
          <div style={styles.detailSection}>
            <strong>Routing Mode:</strong> {detail.decision?.routing_mode}
          </div>
          <div style={styles.detailSection}>
            <strong>Selected Agents:</strong>{' '}
            {(detail.decision?.selected_agents || []).join(', ')}
          </div>
          <div style={styles.detailSection}>
            <strong>Agent Results:</strong>
            <pre style={styles.pre}>
              {JSON.stringify(detail.decision?.agent_results, null, 2)}
            </pre>
          </div>
          <div style={styles.detailSection}>
            <strong>Final Response:</strong>
            <pre style={styles.pre}>
              {JSON.stringify(detail.decision?.final_response, null, 2)}
            </pre>
          </div>
          <div style={styles.detailSection}>
            <strong>Hallucination Audits ({detail.audits?.length || 0}):</strong>
            {detail.audits && detail.audits.length > 0 ? (
              detail.audits.map((a: any, i: number) => (
                <div key={i} style={styles.auditBox}>
                  <div>
                    Agent: <code>{a.agent_code}</code> —{' '}
                    <span style={a.check_passed ? styles.okBadge : styles.violationBadge}>
                      {a.check_passed ? 'PASSED' : 'BLOCKED'}
                    </span>
                  </div>
                  <div>
                    Confidence: {a.confidence?.toFixed(2)} / floor{' '}
                    {a.confidence_floor?.toFixed(2)}
                  </div>
                  <div>Sources: {(a.sources_cited || []).join(', ') || '(none)'}</div>
                  {a.blocked_reason && (
                    <div style={styles.errorText}>Reason: {a.blocked_reason}</div>
                  )}
                </div>
              ))
            ) : (
              <div style={styles.empty}>No hallucination audits recorded for this decision.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function AgentsTab({ agents }: { agents: AgentSpecialtyRow[] }) {
  if (agents.length === 0) {
    return <div style={styles.empty}>No agent specialty bindings found.</div>;
  }
  return (
    <div>
      <p style={styles.bodyText}>
        The agent specialty map is the single source of truth for which agent
        handles which domain. An agent may not respond outside its declared
        specialty. The map below is mirrored in both the TypeScript file
        (<code>agentSpecialtyMap.ts</code>) and the database table
        (<code>agent_specialty_bindings</code>).
      </p>
      <div className="irol-table-wrap"><table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Code</th>
            <th style={styles.th}>Name</th>
            <th style={styles.th}>Specialty</th>
            <th style={styles.th}>Features</th>
            <th style={styles.th}>Task Types</th>
            <th style={styles.th}>Multi-Agent</th>
            <th style={styles.th}>Priority</th>
          </tr>
        </thead>
        <tbody>
          {agents.map((a) => (
            <tr key={a.agent_code} style={styles.tr}>
              <td style={styles.tdMono}>
                <strong>{a.agent_code}</strong>
              </td>
              <td style={styles.td}>{a.agent_name}</td>
              <td style={styles.tdMono}>{a.specialty}</td>
              <td style={styles.td}>{(a.features || []).join(', ')}</td>
              <td style={styles.tdMono}>{(a.task_types || []).join(', ')}</td>
              <td style={styles.td}>
                {a.multi_agent_eligible ? (
                  <span className="irol-status-badge" style={styles.okBadge}>Yes</span>
                ) : (
                  <span className="irol-status-badge" style={styles.violationBadge}>No</span>
                )}
              </td>
              <td style={styles.tdMono}>{a.priority}</td>
            </tr>
          ))}
        </tbody>
      </table></div>
    </div>
  );
}

// ─── helpers ──────────────────────────────────────────────────────
function pct(numerator: number, denominator: number): string {
  if (denominator === 0) return '0%';
  return `${((numerator / denominator) * 100).toFixed(1)}%`;
}

function modeBadge(mode: string): React.CSSProperties {
  const base: React.CSSProperties = {
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '10px',
    fontWeight: 600,
  };
  if (mode === 'multi_agent_fanout') return { ...base, backgroundColor: '#e8f3ec', color: '#4d8760' };
  if (mode === 'copilot_fallback') return { ...base, backgroundColor: '#fdf3e3', color: '#a58340' };
  return { ...base, backgroundColor: '#eef2f7', color: '#46739f' };
}

function StatCard({
  label,
  value,
  color,
  sublabel,
}: {
  label: string;
  value: number;
  color: string;
  sublabel?: string;
}) {
  return (
    <div style={{ ...styles.statCard, borderLeft: `4px solid ${color}` }}>
      <div style={styles.statValue}>{value.toLocaleString()}</div>
      <div style={styles.statLabel}>{label}</div>
      {sublabel && <div style={styles.statSub}>{sublabel}</div>}
    </div>
  );
}

// ─── styles ───────────────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  container: { fontFamily: 'Inter, system-ui, sans-serif', color: '#1d1c1a' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  title: { fontSize: '22px', fontWeight: 700, color: '#1d1c1a', margin: 0 },
  headerActions: { display: 'flex', gap: '8px' },
  refreshBtn: {
    padding: '6px 12px',
    border: '1px solid #c1bba8',
    background: '#fff',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  tabs: {
    display: 'flex',
    gap: '4px',
    borderBottom: '2px solid #eaeae7',
    marginBottom: '16px',
  },
  tab: {
    padding: '8px 16px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#7e7c75',
    borderBottom: '2px solid transparent',
  },
  tabActive: {
    padding: '8px 16px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#766d54',
    borderBottom: '2px solid #766d54',
    fontWeight: 600,
  },
  errorBox: {
    padding: '10px 14px',
    background: '#fbeae8',
    border: '1px solid #a9544c',
    borderRadius: '6px',
    color: '#a9544c',
    marginBottom: '12px',
    fontSize: '13px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '12px',
    marginBottom: '24px',
  },
  statCard: {
    background: '#fff',
    padding: '16px',
    borderRadius: '6px',
    border: '1px solid #eaeae7',
  },
  statValue: { fontSize: '28px', fontWeight: 700, color: '#1d1c1a' },
  statLabel: { fontSize: '12px', color: '#7e7c75', marginTop: '4px' },
  statSub: { fontSize: '11px', color: '#9d8a51', marginTop: '2px' },
  section: {
    background: '#fff',
    padding: '16px',
    borderRadius: '6px',
    border: '1px solid #eaeae7',
    marginBottom: '16px',
  },
  sectionTitle: { fontSize: '15px', fontWeight: 600, color: '#766d54', marginTop: 0 },
  bodyText: { fontSize: '13px', lineHeight: '1.6', color: '#1d1c1a' },
  orderedList: { fontSize: '13px', lineHeight: '1.7', color: '#1d1c1a', paddingLeft: '20px' },
  bulletList: { fontSize: '13px', lineHeight: '1.7', color: '#1d1c1a', paddingLeft: '20px' },
  decisionsLayout: { display: 'flex', gap: '12px' },
  decisionsList: { flex: '1 1 60%' },
  detailPanel: {
    flex: '1 1 40%',
    background: '#fff',
    border: '1px solid #eaeae7',
    borderRadius: '6px',
    padding: '12px',
    maxHeight: '600px',
    overflowY: 'auto',
    fontSize: '12px',
  },
  detailTitle: { fontSize: '14px', fontWeight: 600, color: '#766d54', marginTop: 0 },
  detailSection: { marginBottom: '10px' },
  pre: {
    background: '#f5f4f3',
    padding: '8px',
    borderRadius: '4px',
    fontSize: '11px',
    overflowX: 'auto',
    margin: '4px 0',
  },
  auditBox: {
    background: '#f5f4f3',
    padding: '8px',
    borderRadius: '4px',
    marginBottom: '6px',
    fontSize: '11px',
  },
  errorText: { color: '#a9544c', marginTop: '4px' },
  empty: { color: '#7e7c75', fontStyle: 'italic', padding: '20px' },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '12px',
  },
  th: {
    textAlign: 'left',
    padding: '8px 6px',
    borderBottom: '2px solid #766d54',
    color: '#766d54',
    fontWeight: 600,
    fontSize: '11px',
    textTransform: 'uppercase',
  },
  tr: { cursor: 'pointer', borderBottom: '1px solid #eaeae7' },
  td: { padding: '6px', verticalAlign: 'top' },
  tdMono: { padding: '6px', fontFamily: 'ui-monospace, monospace', fontSize: '11px' },
  violationBadge: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: '8px',
    background: '#a9544c',
    color: '#fff',
    fontSize: '10px',
    fontWeight: 600,
  },
  okBadge: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: '8px',
    background: '#e8f3ec',
    color: '#4d8760',
    fontSize: '10px',
    fontWeight: 600,
  },
  footer: {
    marginTop: '24px',
    padding: '12px',
    background: '#f5f4f3',
    borderRadius: '6px',
    borderLeft: '3px solid #9d8a51',
  },
  footerText: { fontSize: '11px', color: '#7e7c75', lineHeight: '1.5' },
};

export default IntelligentRoutingPanel;
